import { CommonModule } from '@angular/common'
import { HttpClient } from '@angular/common/http'
import {
  AfterViewInit,
  Component,
  ElementRef,
  EventEmitter,
  inject,
  Input,
  OnChanges,
  OnDestroy,
  Output,
  SimpleChanges,
  ViewChild,
} from '@angular/core'
import * as L from 'leaflet'
import 'leaflet/dist/leaflet.css'
import {
  TablotAffair,
  PoolMarker,
  TechnicianHome,
} from '../affair-pool-optimization/affair-pool-optimization.component.model'
import {
  TablotTour,
  TablotTourStep,
} from '../../../interventions/services/interventions.service'
import { SectorModel } from '../../../../core/models/sector.model'

type PointTuple = [number, number]

/** Point unique d'un groupe, avec l'affaire du vivier associée si connue. */
type SingleGroupPoint = { point: PointTuple; affairId: number | null }

/**
 * Marqueur losange dessiné sur canvas : même API et mêmes performances qu'un
 * CircleMarker (détection de clic par rayon incluse), mais forme distincte.
 * Utilisé pour les interventions programmées afin qu'elles ne puissent jamais
 * être confondues avec les points ronds du vivier, même à couleur proche.
 */
const DiamondMarker = L.CircleMarker.extend({
  _updatePath: function (this: any) {
    const renderer = this._renderer
    if (!renderer || !renderer._drawing || this._empty()) return

    const p = this._point
    const r = Math.max(Math.round(this._radius), 1)
    const ctx = renderer._ctx

    ctx.beginPath()
    ctx.moveTo(p.x, p.y - r)
    ctx.lineTo(p.x + r, p.y)
    ctx.lineTo(p.x, p.y + r)
    ctx.lineTo(p.x - r, p.y)
    ctx.closePath()
    renderer._fillStroke(ctx, this)
  },
}) as unknown as typeof L.CircleMarker

/** Intervention déjà programmée affichée en surimpression (contexte d'un secteur). */
export interface ScheduledInterventionMarker {
  id?: number
  latitude: number
  longitude: number
  label?: string | null
  date?: string | null
  technicianName?: string | null
  technicianId?: number | null
  technicianEmail?: string | null
  /** False si le point est hors du secteur actif (affiché atténué, jamais masqué). */
  inSector?: boolean
}

/** Itinéraire (tournée du jour) tracé au clic d'une intervention programmée. */
export interface ScheduledRoute {
  /** Coordonnées GeoJSON renvoyées par le back : [lng, lat]. */
  coordinates: [number, number][]
  color?: string | null
}

/** Résultat de la recherche rapide de ville (API Adresse data.gouv.fr). */
interface CitySearchResult {
  name: string
  context: string
  latitude: number
  longitude: number
}

/** Ville sélectionnée dans la recherche rapide (émise au parent). */
export interface CitySelection {
  name: string
  latitude: number
  longitude: number
}

@Component({
  selector: 'app-tablot-map',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './tablot-map.component.html',
  styleUrl: './tablot-map.component.scss',
})
export class TablotMapComponent implements AfterViewInit, OnChanges, OnDestroy {
  @Input({ required: true }) affairs: TablotAffair[] = []
  @Input() interventions: any[] = []
  @Input() selectedAffair: TablotAffair | null = null
  @Input() selectedIntervention: any = null
  @Input() focusZoom = 12
  @Input() tours: TablotTour[] = []
  @Input() selectedTour: TablotTour | null = null
  @Input() poolMarkers: PoolMarker[] = []
  @Input() technicianHomes: TechnicianHome[] = []
  /** Tous les secteurs : leurs contours sont toujours affichés. */
  @Input() sectors: SectorModel[] = []
  /** Id du secteur actif (mis en avant ; les autres restent visibles, atténués). */
  @Input() selectedSectorId: number | null = null
  /** Interventions déjà programmées à afficher en contexte (déjà filtrées par secteur). */
  @Input() scheduledInterventions: ScheduledInterventionMarker[] = []

  /** Itinéraire de la tournée du jour à tracer (null = aucun). */
  @Input() scheduledRoute: ScheduledRoute | null = null

  /** Ordre des points de la tournée affichée : id intervention → n° d'ordre. */
  @Input() scheduledRouteOrder: Record<number, number> | null = null

  @Output() tourSelected = new EventEmitter<TablotTour>()
  @Output() poolMarkerClicked = new EventEmitter<number>()
  @Output() hiddenGroupsChange = new EventEmitter<Set<string>>()
  /** Émet l'id de l'intervention programmée cliquée. */
  @Output() scheduledInterventionClicked = new EventEmitter<number>()
  /** Émet la ville choisie dans la recherche rapide (null quand elle est effacée). */
  @Output() citySelected = new EventEmitter<CitySelection | null>()

  @ViewChild('mapContainer', { static: true })
  mapContainer!: ElementRef<HTMLDivElement>

  private readonly maxDisplayedInterventions = 6
  private readonly maxDistanceKm = 50
  private readonly maxLinkedInterventions = 3

  /** Color palette for tours (up to 12 distinct colors) */
  private readonly tourColors = [
    '#22c55e',
    '#3b82f6',
    '#f59e0b',
    '#ef4444',
    '#a855f7',
    '#06b6d4',
    '#ec4899',
    '#84cc16',
    '#f97316',
    '#6366f1',
    '#14b8a6',
    '#e11d48',
  ]

  /**
   * Palette des groupes d'affaires du vivier.
   * Volontairement SANS violet/fuchsia/indigo : le violet (#8b5cf6) est réservé
   * aux interventions programmées (losanges) pour éviter toute confusion.
   */
  private readonly groupColors = [
    '#2563eb', // bleu
    '#34d399', // emerald
    '#fb923c', // orange
    '#f472b6', // rose
    '#38bdf8', // ciel
    '#2dd4bf', // teal
    '#facc15', // jaune
    '#4ade80', // vert
    '#f87171', // rouge
    '#a16207', // brun doré
  ]

  private http = inject(HttpClient)

  private map: L.Map | null = null
  private displayLayer = L.layerGroup()
  private toursLayer = L.layerGroup()
  private poolLayer = L.layerGroup()
  private homesLayer = L.layerGroup()
  private sectorLayer = L.layerGroup()
  private scheduledLayer = L.layerGroup()
  private scheduledRouteLayer = L.layerGroup()
  private flashLayer = L.layerGroup()
  private flashClearTimer: ReturnType<typeof setTimeout> | null = null

  /**
   * Renderer canvas partagé : tous les points (vivier + interventions
   * programmées) et les polygones de secteur sont dessinés dans un seul
   * <canvas>, ce qui garde le déplacement fluide même avec 1000+ points
   * (aucun nœud DOM par point, contrairement aux divIcon).
   */
  private canvasRenderer = L.canvas({ padding: 0.5 })

  /** Fond de carte statique : limites + numéros de départements (france.json). */
  private departmentsLayer = L.layerGroup()
  private departmentsRequested = false

  /** Marqueurs canvas dont le rayon s'adapte au zoom (lisibilité à 1000+ points). */
  private zoomScaledMarkers: { marker: L.CircleMarker; base: number }[] = []

  /**
   * Signature du dernier recadrage automatique : on ne re-fit la carte
   * qu'au changement de contexte (secteur, tournée…), jamais pendant une
   * simple mise à jour de données, pour ne pas interrompre la navigation.
   */
  private lastFitKey: string | null = null

  hiddenGroups = new Set<string>()

  // ───── Recherche rapide de ville (API Adresse data.gouv.fr) ─────

  citySearchQuery = ''
  citySearchResults: CitySearchResult[] = []
  citySearchOpen = false
  citySearchLoading = false
  citySearchActiveIndex = -1
  private citySearchTimer: ReturnType<typeof setTimeout> | null = null
  /** N° de requête : seule la réponse de la dernière requête est prise en compte. */
  private citySearchSeq = 0

  onCitySearchInput(value: string): void {
    this.citySearchQuery = value
    if (this.citySearchTimer) clearTimeout(this.citySearchTimer)

    const q = value.trim()
    if (q.length < 2) {
      this.citySearchSeq++
      this.citySearchResults = []
      this.citySearchOpen = false
      this.citySearchLoading = false
      return
    }
    // Debounce : une requête max toutes les 250 ms pendant la frappe.
    this.citySearchTimer = setTimeout(() => this.fetchCities(q), 250)
  }

  private fetchCities(q: string): void {
    const seq = ++this.citySearchSeq
    this.citySearchLoading = true

    // Géoplateforme (data.geopf.fr) : successeur de api-adresse.data.gouv.fr
    // (BAN historique décommissionnée — renvoie des réponses vides).
    const url =
      'https://data.geopf.fr/geocodage/search' +
      `?q=${encodeURIComponent(q)}&type=municipality&autocomplete=1&limit=6`

    this.http.get<any>(url).subscribe({
      next: (res) => {
        if (seq !== this.citySearchSeq) return // réponse obsolète
        this.citySearchLoading = false
        this.citySearchResults = ((res?.features ?? []) as any[])
          .map((f): CitySearchResult | null => {
            const props = f?.properties ?? {}
            const coords = f?.geometry?.coordinates
            if (!Array.isArray(coords) || coords.length < 2) return null
            const name = props.city ?? props.name ?? props.label ?? ''
            if (!name) return null
            // context = "69, Rhône, Auvergne-Rhône-Alpes" → on garde CP + département.
            const dept = String(props.context ?? '')
              .split(',')
              .slice(0, 2)
              .map((s: string) => s.trim())
              .join(' — ')
            return {
              name,
              context: [props.postcode, dept].filter(Boolean).join(' · '),
              latitude: coords[1],
              longitude: coords[0],
            }
          })
          .filter((c): c is CitySearchResult => c !== null)
        this.citySearchOpen = this.citySearchResults.length > 0
        this.citySearchActiveIndex = this.citySearchResults.length > 0 ? 0 : -1
      },
      error: () => {
        if (seq !== this.citySearchSeq) return
        this.citySearchLoading = false
        this.citySearchResults = []
        this.citySearchOpen = false
      },
    })
  }

  onCitySearchKeydown(event: KeyboardEvent): void {
    if (!this.citySearchOpen || this.citySearchResults.length === 0) {
      if (event.key === 'Escape') this.clearCitySearch()
      return
    }

    switch (event.key) {
      case 'ArrowDown':
        event.preventDefault()
        this.citySearchActiveIndex =
          (this.citySearchActiveIndex + 1) % this.citySearchResults.length
        break
      case 'ArrowUp':
        event.preventDefault()
        this.citySearchActiveIndex =
          (this.citySearchActiveIndex - 1 + this.citySearchResults.length) %
          this.citySearchResults.length
        break
      case 'Enter': {
        event.preventDefault()
        const city =
          this.citySearchResults[this.citySearchActiveIndex] ??
          this.citySearchResults[0]
        if (city) this.selectCity(city)
        break
      }
      case 'Escape':
        this.citySearchOpen = false
        break
    }
  }

  /** mousedown (et non click) : se déclenche avant le blur de l'input. */
  selectCity(city: CitySearchResult, event?: MouseEvent): void {
    event?.preventDefault()
    this.citySearchQuery = city.name
    this.citySearchOpen = false
    this.citySearchActiveIndex = -1
    // Zoom + flash visuel sur la ville (réutilise le comportement existant).
    this.panTo(city.latitude, city.longitude, 12)
    this.citySelected.emit({
      name: city.name,
      latitude: city.latitude,
      longitude: city.longitude,
    })
  }

  onCitySearchFocus(): void {
    if (this.citySearchResults.length > 0) this.citySearchOpen = true
  }

  onCitySearchBlur(): void {
    // Léger délai : laisse le mousedown d'un résultat s'exécuter avant la fermeture.
    setTimeout(() => (this.citySearchOpen = false), 150)
  }

  clearCitySearch(event?: MouseEvent): void {
    event?.preventDefault()
    if (this.citySearchTimer) clearTimeout(this.citySearchTimer)
    this.citySearchSeq++
    this.citySearchQuery = ''
    this.citySearchResults = []
    this.citySearchOpen = false
    this.citySearchLoading = false
    this.citySearchActiveIndex = -1
    this.citySelected.emit(null)
  }

  toggleGroup(name: string): void {
    const next = new Set(this.hiddenGroups)
    if (next.has(name)) {
      next.delete(name)
    } else {
      next.add(name)
    }
    this.hiddenGroups = next
    this.hiddenGroupsChange.emit(new Set(this.hiddenGroups))
    this.renderMap()
  }

  isGroupHidden(name: string): boolean {
    return this.hiddenGroups.has(name)
  }

  /** True si tous les groupes de la légende sont actuellement masqués. */
  get allGroupsHidden(): boolean {
    const items = this.groupLegendItems
    return items.length > 0 && items.every((i) => this.hiddenGroups.has(i.name))
  }

  /**
   * Bascule global : masque tous les groupes d'un coup (puis on rallume
   * individuellement via l'œil), ou réaffiche tout si déjà tout masqué.
   */
  toggleAllGroups(): void {
    const items = this.groupLegendItems
    this.hiddenGroups = this.allGroupsHidden
      ? new Set()
      : new Set(items.map((i) => i.name))
    this.hiddenGroupsChange.emit(new Set(this.hiddenGroups))
    this.renderMap()
  }

  ngAfterViewInit(): void {
    this.createMap()
    this.renderMap()
  }

  ngOnChanges(_changes: SimpleChanges): void {
    if (!this.map) return
    this.renderMap()
  }

  /** Centre la carte sur un point géographique et affiche un flash visuel. */
  panTo(lat: number, lng: number, zoom = 10): void {
    this.map?.flyTo([lat, lng], zoom, { duration: 0.8 })
    this.showFlash(lat, lng)
  }

  private showFlash(lat: number, lng: number): void {
    if (!this.map) return

    // Annuler le timer précédent et nettoyer le layer
    if (this.flashClearTimer !== null) {
      clearTimeout(this.flashClearTimer)
      this.flashClearTimer = null
    }
    this.flashLayer.clearLayers()

    const html = `
      <div style="position:relative;width:28px;height:28px;pointer-events:none;">
        <span class="map-flash-ring"></span>
        <span class="map-flash-ring"></span>
        <span class="map-flash-ring"></span>
      </div>`

    const icon = L.divIcon({
      className: 'map-flash-icon',
      html,
      iconSize: [28, 28],
      iconAnchor: [14, 14],
    })

    L.marker([lat, lng], {
      icon,
      interactive: false,
      zIndexOffset: 2000,
    }).addTo(this.flashLayer)

    // Retirer après la fin de la dernière vague (0.7s delay + 1.2s anim)
    this.flashClearTimer = setTimeout(() => {
      this.flashLayer.clearLayers()
      this.flashClearTimer = null
    }, 2200)
  }

  ngOnDestroy(): void {
    if (this.flashClearTimer !== null) {
      clearTimeout(this.flashClearTimer)
      this.flashClearTimer = null
    }
    if (this.citySearchTimer !== null) {
      clearTimeout(this.citySearchTimer)
      this.citySearchTimer = null
    }
    this.map?.remove()
    this.map = null
  }

  get displayedInterventionsCount(): number {
    return this.getNearbyInterventions().length
  }

  private createMap(): void {
    const franceBounds = L.latLngBounds(
      [41.3, -5.5], // Sud-Ouest (Pyrénées / Atlantique)
      [51.2, 9.8] // Nord-Est (Nord / Alsace)
    )

    this.map = L.map(this.mapContainer.nativeElement, {
      center: [46.2276, 2.2137],
      zoom: 6,
      minZoom: 5,
      maxBounds: franceBounds,
      maxBoundsViscosity: 1.0,
      zoomControl: true,
      attributionControl: true,
      preferCanvas: true,
    })

    L.tileLayer(
      'https://{s}.basemaps.cartocdn.com/light_nolabels/{z}/{x}/{y}{r}.png',
      {
        attribution: '&copy; OpenStreetMap contributors &copy; CARTO',
        subdomains: 'abcd',
        maxZoom: 20,
      }
    ).addTo(this.map)

    L.tileLayer(
      'https://{s}.basemaps.cartocdn.com/light_only_labels/{z}/{x}/{y}{r}.png',
      {
        attribution: '&copy; OpenStreetMap contributors &copy; CARTO',
        subdomains: 'abcd',
        maxZoom: 20,
        pane: 'overlayPane',
      }
    ).addTo(this.map)

    // Pane dédié aux interventions programmées, sous le markerPane (z-index 600)
    // → les marqueurs de tournée et du vivier passent toujours au-dessus.
    this.map.createPane('scheduledPane')
    const scheduledPane = this.map.getPane('scheduledPane')
    if (scheduledPane) scheduledPane.style.zIndex = '450'

    // Panes des départements : simple fond, sous tous les éléments interactifs
    // (overlayPane = 400) et insensible aux clics.
    const depPane = this.map.createPane('departmentsPane')
    depPane.style.zIndex = '340'
    depPane.style.pointerEvents = 'none'
    const depLabelsPane = this.map.createPane('departmentLabelsPane')
    depLabelsPane.style.zIndex = '345'
    depLabelsPane.style.pointerEvents = 'none'

    // Rayon des points adapté au niveau de zoom (dézoomé = points plus petits).
    this.map.on('zoomend', () => this.applyZoomScaledRadii())

    this.departmentsLayer.addTo(this.map)
    this.loadDepartments()

    this.sectorLayer.addTo(this.map)
    this.scheduledRouteLayer.addTo(this.map)
    this.scheduledLayer.addTo(this.map)
    this.displayLayer.addTo(this.map)
    this.toursLayer.addTo(this.map)
    this.poolLayer.addTo(this.map)
    this.homesLayer.addTo(this.map)
    this.flashLayer.addTo(this.map)
  }

  // ───── Départements (fond statique, chargé une seule fois) ─────

  private loadDepartments(): void {
    if (this.departmentsRequested) return
    this.departmentsRequested = true
    // france.json est servi depuis public/ (cf. sector-settings / heat-map).
    this.http.get('france.json').subscribe({
      next: (geo) => this.renderDepartments(geo),
      // Fond facultatif : en cas d'échec, la carte reste utilisable.
      error: () => {},
    })
  }

  private renderDepartments(geo: any): void {
    if (!this.map) return
    this.departmentsLayer.clearLayers()

    // Limites des départements (traits fins, non interactifs).
    L.geoJSON(geo, {
      pane: 'departmentsPane',
      interactive: false,
      style: {
        color: '#94a3b8',
        weight: 1,
        opacity: 0.55,
        fill: false,
      },
    }).addTo(this.departmentsLayer)

    // Numéro de chaque département, placé au centroïde de son plus grand anneau.
    for (const feature of geo?.features ?? []) {
      const code = feature?.properties?.code
      const center = this.polygonLabelPoint(feature?.geometry)
      if (!code || !center) continue

      const icon = L.divIcon({
        className: 'dept-code-label',
        html: `<span>${this.escapeHtml(String(code))}</span>`,
        iconSize: [26, 14],
        iconAnchor: [13, 7],
      })
      L.marker(center, {
        icon,
        pane: 'departmentLabelsPane',
        interactive: false,
        keyboard: false,
      }).addTo(this.departmentsLayer)
    }
  }

  /** Point de label d'une géométrie GeoJSON (centroïde du plus grand anneau). */
  private polygonLabelPoint(geometry: any): PointTuple | null {
    if (!geometry) return null
    let rings: number[][][] = []
    if (geometry.type === 'Polygon') {
      rings = [geometry.coordinates?.[0]]
    } else if (geometry.type === 'MultiPolygon') {
      rings = (geometry.coordinates ?? []).map((p: number[][][]) => p?.[0])
    }

    let best: number[][] | null = null
    let bestArea = 0
    for (const ring of rings) {
      if (!ring || ring.length < 3) continue
      const area = Math.abs(this.ringArea(ring))
      if (area > bestArea) {
        bestArea = area
        best = ring
      }
    }
    return best ? this.ringCentroid(best) : null
  }

  /** Aire signée (formule du lacet) d'un anneau GeoJSON [lng, lat]. */
  private ringArea(ring: number[][]): number {
    let sum = 0
    for (let i = 0; i < ring.length - 1; i++) {
      const [x1, y1] = ring[i]
      const [x2, y2] = ring[i + 1]
      sum += x1 * y2 - x2 * y1
    }
    return sum / 2
  }

  /** Centroïde d'un anneau GeoJSON [lng, lat] → [lat, lng]. */
  private ringCentroid(ring: number[][]): PointTuple | null {
    const area = this.ringArea(ring)
    if (area === 0) return null
    let cx = 0
    let cy = 0
    for (let i = 0; i < ring.length - 1; i++) {
      const [x1, y1] = ring[i]
      const [x2, y2] = ring[i + 1]
      const cross = x1 * y2 - x2 * y1
      cx += (x1 + x2) * cross
      cy += (y1 + y2) * cross
    }
    return [cy / (6 * area), cx / (6 * area)]
  }

  // ───── Secteurs (tous toujours affichés) ─────

  /** Dessine les contours de tous les secteurs ; le secteur actif est mis en avant. */
  private renderSectors(): void {
    this.sectorLayer.clearLayers()
    if (!this.sectors?.length) return

    const hasSelection = this.selectedSectorId != null

    for (const sector of this.sectors) {
      const rings = sector.geometry?.coordinates
      if (!rings?.length || !rings[0] || rings[0].length < 3) continue

      // GeoJSON = [lng, lat] → Leaflet = [lat, lng]
      const latlngs = rings.map((ring) =>
        ring.map(([lng, lat]) => [lat, lng] as PointTuple)
      )
      const isSelected = sector.id === this.selectedSectorId
      const color = sector.color || '#6366f1'

      L.polygon(latlngs, {
        renderer: this.canvasRenderer,
        color,
        weight: isSelected ? 3 : 1.5,
        opacity: hasSelection ? (isSelected ? 0.9 : 0.35) : 0.7,
        fillColor: color,
        fillOpacity: hasSelection ? (isSelected ? 0.06 : 0.015) : 0.03,
        dashArray: isSelected ? undefined : '6, 6',
        interactive: false,
      }).addTo(this.sectorLayer)
    }
  }

  /** Secteur actif (le cas échéant). */
  private get selectedSector(): SectorModel | null {
    if (this.selectedSectorId == null) return null
    return this.sectors.find((s) => s.id === this.selectedSectorId) ?? null
  }

  // ───── Rayon des points adapté au zoom ─────

  private zoomRadiusFactor(): number {
    const z = this.map?.getZoom() ?? 6
    if (z >= 11) return 1.35
    if (z >= 9) return 1.15
    if (z >= 8) return 1
    if (z >= 7) return 0.85
    return 0.7
  }

  private applyZoomScaledRadii(): void {
    const factor = this.zoomRadiusFactor()
    for (const { marker, base } of this.zoomScaledMarkers) {
      marker.setRadius(base * factor)
    }
  }

  /** Affiche les interventions déjà programmées (points violets, toujours visibles). */
  private renderScheduledInterventions(): void {
    const order = this.scheduledRouteOrder
    const hasRoute = !!order && Object.keys(order).length > 0
    const factor = this.zoomRadiusFactor()

    for (const it of this.scheduledInterventions ?? []) {
      if (it.latitude == null || it.longitude == null) continue

      // N° d'ordre dans la tournée affichée (le cas échéant).
      const stepNum = it.id != null ? (order?.[it.id] ?? null) : null

      let marker: L.Marker | L.CircleMarker
      if (stepNum != null) {
        // Point appartenant à la tournée affichée → pastille numérotée (divIcon :
        // ils sont peu nombreux, le coût DOM est négligeable).
        const icon = L.divIcon({
          className: 'scheduled-intervention-marker si-numbered',
          html: `<span class="si-num">${stepNum}</span>`,
          iconSize: [22, 22],
          iconAnchor: [11, 11],
        })
        marker = L.marker([it.latitude, it.longitude], {
          icon,
          zIndexOffset: 100,
          // Pane dédié (z-index 450) : toujours sous les marqueurs de tournée
          // (numéros) et du vivier, qui sont dans le markerPane (z-index 600).
          pane: 'scheduledPane',
        })
      } else {
        // Point standard : LOSANGE violet dessiné sur le canvas partagé
        // (performant à 1000+ points). La forme le distingue toujours des
        // points ronds du vivier, même à couleur proche.
        // Atténué si une tournée est affichée ou hors secteur actif.
        const dimmed = hasRoute || it.inSector === false
        const base = 6
        const diamond = new DiamondMarker([it.latitude, it.longitude], {
          renderer: this.canvasRenderer,
          radius: base * factor,
          color: '#ffffff',
          weight: 1.25,
          opacity: dimmed ? 0.3 : 0.9,
          fillColor: '#8b5cf6',
          fillOpacity: dimmed ? 0.25 : 0.85,
        })
        this.zoomScaledMarkers.push({ marker: diamond, base })
        marker = diamond
      }

      // Clic → tracer la tournée du jour du technicien (géré par le parent).
      if (it.id != null) {
        const interventionId = it.id
        marker.on('click', () =>
          this.scheduledInterventionClicked.emit(interventionId)
        )
      }

      const dateLabel = this.formatScheduledDate(it.date)
      const rows: string[] = []
      if (dateLabel) {
        rows.push(
          `<div class="si-tt-date"><i class="pi pi-calendar"></i>${this.escapeHtml(dateLabel)}</div>`
        )
      }
      if (it.label && it.label.trim()) {
        rows.push(`<div class="si-tt-title">${this.escapeHtml(it.label)}</div>`)
      }
      if (it.technicianName && it.technicianName.trim()) {
        rows.push(
          `<div class="si-tt-tech"><i class="pi pi-user"></i>${this.escapeHtml(it.technicianName)}</div>`
        )
      }
      if (rows.length > 0) {
        marker.bindTooltip(`<div class="si-tt">${rows.join('')}</div>`, {
          direction: 'top',
          offset: [0, -8],
          opacity: 1,
          className: 'si-tooltip',
        })
      }
      marker.addTo(this.scheduledLayer)
    }
  }

  /** Trace l'itinéraire (tournée du jour) renvoyé par le back, au clic. */
  private renderScheduledRoute(): void {
    const route = this.scheduledRoute
    if (!route?.coordinates?.length) return

    // GeoJSON = [lng, lat] → Leaflet = [lat, lng]
    const latLngs = route.coordinates.map(
      ([lng, lat]) => [lat, lng] as PointTuple
    )
    // IMPORTANT : renderer partagé. Un canvas séparé se superposerait aux
    // points et avalerait leurs clics (Leaflet ne propage pas les événements
    // entre canvas empilés).
    L.polyline(latLngs, {
      renderer: this.canvasRenderer,
      color: route.color || '#8b5cf6',
      weight: 4,
      opacity: 0.85,
      lineCap: 'round',
      lineJoin: 'round',
      dashArray: '8, 6',
      interactive: false,
    }).addTo(this.scheduledRouteLayer)
  }

  /** Formate une date/heure d'intervention en "JJ/MM/AAAA" (+ "HH:MM" si renseignée). */
  private formatScheduledDate(value?: string | null): string {
    if (!value) return ''
    const d = new Date(value)
    if (Number.isNaN(d.getTime())) return value.substring(0, 10)
    const pad = (n: number) => String(n).padStart(2, '0')
    const datePart = `${pad(d.getDate())}/${pad(d.getMonth() + 1)}/${d.getFullYear()}`
    // Heure omise si minuit pile (intervention sans horaire précis)
    if (d.getHours() === 0 && d.getMinutes() === 0) return datePart
    return `${datePart} à ${pad(d.getHours())}:${pad(d.getMinutes())}`
  }

  private renderMap(): void {
    if (!this.map) return

    this.displayLayer.clearLayers()
    this.toursLayer.clearLayers()
    this.poolLayer.clearLayers()
    this.homesLayer.clearLayers()
    this.scheduledLayer.clearLayers()
    this.scheduledRouteLayer.clearLayers()
    this.zoomScaledMarkers = []

    // Recadrage uniquement au changement de contexte (secteur / tournée / arrivée
    // des données) : jamais lors d'un simple rafraîchissement, pour ne pas
    // interrompre le déplacement manuel de l'utilisateur.
    const fitKey = this.computeFitKey()
    const shouldFit = fitKey !== this.lastFitKey
    this.lastFitKey = fitKey

    // Ordre de dessin dans le canvas partagé = ordre d'insertion :
    // secteurs → tracés (tournée, itinéraire) → points programmés → points vivier.
    // Les lignes sont donc SOUS les points, et tous les éléments cliquables
    // partagent le même canvas (indispensable pour la détection des clics).
    this.renderSectors()

    const hasTours = this.tours.length > 0
    if (hasTours) {
      this.renderTours(shouldFit)
    }

    this.renderScheduledRoute()
    this.renderScheduledInterventions()
    this.renderTechnicianHomes()
    this.renderPoolMarkers()

    if (hasTours) return

    if (!this.affairs?.length) {
      if (shouldFit) this.fitToContext()
      requestAnimationFrame(() => this.map?.invalidateSize())
      return
    }

    const selectedAffair = this.selectedAffair ?? this.affairs?.[0] ?? null
    const selectedCoords = selectedAffair
      ? this.getAffairCoordinates(selectedAffair)
      : null
    const nearbyInterventions = this.getNearbyInterventions()

    if (!selectedAffair || !selectedCoords) {
      this.map.setView([46.2276, 2.2137], 6)
      requestAnimationFrame(() => this.map?.invalidateSize())
      return
    }

    const groupColorMap = this.buildGroupColorMap()

    // Render non-selected affairs (dimmed background dots)
    ;(this.affairs ?? []).forEach((affair) => {
      if (affair.id === selectedAffair.id) return
      const coords = this.getAffairCoordinates(affair)
      if (!coords) return
      const color = this.getGroupColor(affair.group_name, groupColorMap)
      const mark = L.circleMarker(coords, {
        renderer: this.canvasRenderer,
        radius: 5,
        color,
        weight: 1.5,
        fillColor: color,
        fillOpacity: 0.35,
      })
      mark.bindPopup(this.buildAffairPopup(affair))
      mark.addTo(this.displayLayer)
    })

    // Render selected affair (highlighted)
    const selectedColor = this.getGroupColor(
      selectedAffair.group_name,
      groupColorMap
    )
    const affairMarker = L.circleMarker(selectedCoords, {
      renderer: this.canvasRenderer,
      radius: 9,
      color: selectedColor,
      weight: 3,
      fillColor: selectedColor,
      fillOpacity: 0.98,
    })
    affairMarker.bindPopup(this.buildAffairPopup(selectedAffair))
    affairMarker.addTo(this.displayLayer)

    nearbyInterventions.forEach(
      ({ intervention, coords, distanceKm }, index) => {
        const isSelected = intervention?.id === this.selectedIntervention?.id

        if (index < this.maxLinkedInterventions) {
          L.polyline([selectedCoords, coords], {
            renderer: this.canvasRenderer,
            color: isSelected ? '#f97316' : '#94a3b8',
            weight: isSelected ? 3 : 2,
            opacity: isSelected ? 0.75 : 0.35,
            dashArray: '6 6',
            interactive: false,
          }).addTo(this.displayLayer)
        }

        const marker = L.circleMarker(coords, {
          renderer: this.canvasRenderer,
          radius: isSelected ? 7 : 5,
          color: isSelected ? '#ea580c' : '#fdba74',
          weight: isSelected ? 3 : 2,
          fillColor: isSelected ? '#f97316' : '#fb923c',
          fillOpacity: 0.95,
        })
        marker.bindPopup(this.buildInterventionPopup(intervention, distanceKm))
        marker.addTo(this.displayLayer)
      }
    )

    this.fitMap(selectedCoords, nearbyInterventions)
    requestAnimationFrame(() => this.map?.invalidateSize())
  }

  private fitMap(
    selectedCoords: PointTuple,
    nearbyInterventions: {
      intervention: any
      coords: PointTuple
      distanceKm: number
    }[]
  ): void {
    if (!this.map) return

    const points: L.LatLngExpression[] = [selectedCoords]
    nearbyInterventions.forEach((item) => points.push(item.coords))

    if (points.length === 1) {
      this.map.setView(selectedCoords, this.focusZoom)
      return
    }

    this.map.fitBounds(L.latLngBounds(points), {
      padding: [28, 28],
      maxZoom: this.focusZoom,
    })
  }

  /** Signature du contexte d'affichage : un changement déclenche un recadrage. */
  private computeFitKey(): string {
    const tour = this.selectedTour
    const tourKey = tour ? `${tour.date}:${tour.user_id}` : ''
    const hasPoints =
      this.poolMarkers.length > 0 || this.scheduledInterventions.length > 0
    return `${this.selectedSectorId ?? 'all'}|${tourKey}|${this.tours.length > 0}|${hasPoints}`
  }

  /** Recadre sur le secteur actif, sinon sur l'ensemble des points du vivier. */
  private fitToContext(): void {
    if (!this.map) return

    const sector = this.selectedSector
    const ring = sector?.geometry?.coordinates?.[0]
    if (ring && ring.length >= 3) {
      const latlngs = ring.map(([lng, lat]) => [lat, lng] as PointTuple)
      this.map.fitBounds(L.latLngBounds(latlngs), { padding: [30, 30] })
      return
    }

    const pts: L.LatLngExpression[] = this.poolMarkers
      .filter((pm) => pm.latitude && pm.longitude)
      .map((pm) => [pm.latitude, pm.longitude] as PointTuple)
    if (pts.length > 0) {
      this.map.fitBounds(L.latLngBounds(pts), {
        padding: [40, 40],
        maxZoom: 10,
      })
      return
    }

    this.map.setView([46.2276, 2.2137], 6)
  }

  private getNearbyInterventions(): {
    intervention: any
    coords: PointTuple
    distanceKm: number
  }[] {
    const selectedAffair = this.selectedAffair ?? this.affairs?.[0] ?? null
    const selectedCoords = selectedAffair
      ? this.getAffairCoordinates(selectedAffair)
      : null

    if (!selectedCoords) return []

    return (this.interventions ?? [])
      .map((intervention) => {
        const coords = this.getInterventionCoordinates(intervention)
        if (!coords) return null

        return {
          intervention,
          coords,
          distanceKm: this.haversineKm(
            selectedCoords[0],
            selectedCoords[1],
            coords[0],
            coords[1]
          ),
        }
      })
      .filter(Boolean)
      .filter((item) => item!.distanceKm <= this.maxDistanceKm)
      .sort((a, b) => a!.distanceKm - b!.distanceKm)
      .slice(0, this.maxDisplayedInterventions) as {
      intervention: any
      coords: PointTuple
      distanceKm: number
    }[]
  }

  private getAffairCoordinates(affair: TablotAffair): PointTuple | null {
    const lat = this.toNumber(affair.work_site_latitude)
    const lng = this.toNumber(affair.work_site_longitude)
    if (lat == null || lng == null) return null
    return [lat, lng]
  }

  private getInterventionCoordinates(intervention: any): PointTuple | null {
    const lat = this.toNumber(
      intervention?.affair?.workSiteLatitude ??
        intervention?.affair?.work_site_latitude ??
        intervention?.workSiteLatitude ??
        intervention?.work_site_latitude ??
        intervention?.data?.workSiteLatitude
    )
    const lng = this.toNumber(
      intervention?.affair?.workSiteLongitude ??
        intervention?.affair?.work_site_longitude ??
        intervention?.workSiteLongitude ??
        intervention?.work_site_longitude ??
        intervention?.data?.workSiteLongitude
    )
    if (lat == null || lng == null) return null
    return [lat, lng]
  }

  private buildAffairPopup(affair: TablotAffair): string {
    return `
      <div class="tablot-popup">
        <strong>${this.escapeHtml(affair.names)}</strong><br />
        <span>${this.escapeHtml(affair.work_site_address)}</span><br />
        <span>${this.escapeHtml(
          `${affair.work_site_postal_code} ${affair.work_site_locality}`
        )}</span>
      </div>
    `
  }

  private buildInterventionPopup(
    intervention: any,
    distanceKm: number
  ): string {
    const name = intervention?.name ?? intervention?.title ?? 'Intervention'
    const locality =
      intervention?.affair?.workSiteLocality ??
      intervention?.workSiteLocality ??
      intervention?.data?.workSiteLocality ??
      ''

    return `
      <div class="tablot-popup">
        <strong>${this.escapeHtml(name)}</strong><br />
        <span>${this.escapeHtml(locality)}</span><br />
        <span>${distanceKm.toFixed(1)} km</span>
      </div>
    `
  }

  private escapeHtml(value: string): string {
    return value
      .replaceAll('&', '&amp;')
      .replaceAll('<', '&lt;')
      .replaceAll('>', '&gt;')
      .replaceAll('"', '&quot;')
      .replaceAll("'", '&#39;')
  }

  private toNumber(value: unknown): number | null {
    if (value == null) return null
    const parsed =
      typeof value === 'number'
        ? value
        : parseFloat(String(value).replace(',', '.'))
    return Number.isFinite(parsed) ? parsed : null
  }

  private haversineKm(
    lat1: number,
    lon1: number,
    lat2: number,
    lon2: number
  ): number {
    const earthRadiusKm = 6371
    const deltaLat = this.degToRad(lat2 - lat1)
    const deltaLon = this.degToRad(lon2 - lon1)
    const a =
      Math.sin(deltaLat / 2) * Math.sin(deltaLat / 2) +
      Math.cos(this.degToRad(lat1)) *
        Math.cos(this.degToRad(lat2)) *
        Math.sin(deltaLon / 2) *
        Math.sin(deltaLon / 2)
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a))
    return earthRadiusKm * c
  }

  private degToRad(degrees: number): number {
    return (degrees * Math.PI) / 180
  }

  // ───── Tour rendering ─────

  get tourLegendItems(): { color: string; name: string; date: string }[] {
    return this.tours.map((tour, i) => ({
      color: this.tourColors[i % this.tourColors.length],
      name: tour.technician_name,
      date: tour.date,
    }))
  }

  get groupLegendItems(): {
    color: string
    name: string
    singlePoint: SingleGroupPoint | null
  }[] {
    const colorMap = this.buildGroupColorMap()

    if (this.selectedTour) {
      const tourAffairIds = new Set(
        (this.selectedTour.steps ?? [])
          .filter((s) => s.job_type === 'tablot_affair' && s.affair_id != null)
          .map((s) => s.affair_id!)
      )
      const tourGroups = new Set(
        (this.poolMarkers ?? [])
          .filter(
            (pm) =>
              pm.affairId != null &&
              tourAffairIds.has(pm.affairId) &&
              pm.groupName
          )
          .map((pm) => pm.groupName!)
      )
      return Array.from(colorMap.entries())
        .filter(([name]) => tourGroups.has(name))
        .map(([name, color]) => ({
          name,
          color,
          singlePoint: this.getSingleGroupPoint(name),
        }))
    }

    return Array.from(colorMap.entries()).map(([name, color]) => ({
      name,
      color,
      singlePoint: this.getSingleGroupPoint(name),
    }))
  }

  /**
   * Coordonnées (et affaire du vivier associée, le cas échéant) du point d'un
   * groupe s'il n'en contient qu'un seul (vivier + affaires confondus,
   * dédupliqué par coordonnées), sinon null.
   * Utilisé par la légende pour proposer un bouton "centrer sur ce point".
   */
  private getSingleGroupPoint(groupName: string): SingleGroupPoint | null {
    const points = new Map<string, PointTuple>()
    let affairId: number | null = null

    for (const pm of this.poolMarkers ?? []) {
      if (pm.groupName !== groupName || !pm.latitude || !pm.longitude) continue
      points.set(`${pm.latitude}:${pm.longitude}`, [pm.latitude, pm.longitude])
      if (points.size > 1) return null
      affairId ??= pm.affairId ?? null
    }

    for (const affair of this.affairs ?? []) {
      if ((affair.group_name ?? '') !== groupName) continue
      const coords = this.getAffairCoordinates(affair)
      if (!coords) continue
      points.set(`${coords[0]}:${coords[1]}`, coords)
      if (points.size > 1) return null
    }

    return points.size === 1
      ? { point: points.values().next().value!, affairId }
      : null
  }

  /**
   * Centre la carte sur l'unique point d'un groupe (bouton de la légende) et,
   * s'il s'agit d'une affaire du vivier, déclenche le même comportement qu'un
   * clic sur le point (pré-chargement des créneaux par le parent).
   */
  focusGroupPoint(singlePoint: SingleGroupPoint, event?: MouseEvent): void {
    event?.stopPropagation()
    this.panTo(singlePoint.point[0], singlePoint.point[1], this.focusZoom)
    if (singlePoint.affairId != null) {
      this.poolMarkerClicked.emit(singlePoint.affairId)
    }
  }

  private buildGroupColorMap(): Map<string, string> {
    const fromAffairs = (this.affairs ?? [])
      .map((a) => a.group_name ?? '')
      .filter(Boolean)
    const fromPool = (this.poolMarkers ?? [])
      .map((pm) => pm.groupName ?? '')
      .filter(Boolean)
    const groups = [...new Set([...fromAffairs, ...fromPool])].sort()
    const map = new Map<string, string>()
    groups.forEach((g, i) => {
      map.set(g, this.groupColors[i % this.groupColors.length])
    })
    return map
  }

  private getGroupColor(
    groupName: string | null | undefined,
    colorMap: Map<string, string>
  ): string {
    if (!groupName) return '#2563eb'
    return colorMap.get(groupName) ?? '#2563eb'
  }

  private renderTours(shouldFit = true): void {
    if (!this.map) return

    const allPoints: L.LatLngExpression[] = []

    this.tours.forEach((tour, tourIndex) => {
      const color = this.tourColors[tourIndex % this.tourColors.length]
      const isSelected = this.selectedTour
        ? tour.vehicle_id === this.selectedTour.vehicle_id
        : true
      const opacity = this.selectedTour ? (isSelected ? 1 : 0.25) : 0.85

      // Draw route polyline from encoded geometry
      if (tour.geometry) {
        const latLngs = this.decodePolyline(tour.geometry, 5)
        if (latLngs.length > 0) {
          // Renderer partagé : un canvas séparé au-dessus des points
          // intercepterait leurs clics (cf. renderScheduledRoute).
          const polyline = L.polyline(latLngs, {
            renderer: this.canvasRenderer,
            color,
            weight: isSelected ? 4 : 2.5,
            opacity,
            lineCap: 'round',
            lineJoin: 'round',
            interactive: true,
          })
          polyline.on('click', () => this.tourSelected.emit(tour))
          polyline.bindTooltip(
            `${this.escapeHtml(tour.technician_name)} — ${this.formatTourDate(tour.date)}`,
            { sticky: true, className: 'tour-tooltip' }
          )
          polyline.addTo(this.toursLayer)
          latLngs.forEach((pt) => allPoints.push(pt))
        }
      }

      // Draw step markers
      const jobSteps = tour.steps.filter((s) => s.type === 'job')
      const startStep = tour.steps.find((s) => s.type === 'start')
      const endStep = tour.steps.find((s) => s.type === 'end')

      // Start marker (home)
      if (startStep?.location) {
        const coords: PointTuple = [
          startStep.location[1],
          startStep.location[0],
        ]
        allPoints.push(coords)

        const homeIcon = L.divIcon({
          className: 'tour-step-icon',
          html: `<div style="
            width:30px;height:30px;
            border-radius:50%;
            background:#0f172a;
            border:2.5px solid ${color};
            color:${color};
            font-size:14px;
            display:flex;align-items:center;justify-content:center;
            box-shadow:0 2px 8px rgba(0,0,0,0.35);
            opacity:${this.selectedTour ? (isSelected ? 1 : 0.3) : 1};
          ">🏠</div>`,
          iconSize: [30, 30],
          iconAnchor: [15, 15],
        })

        L.marker(coords, { icon: homeIcon })
          .bindTooltip(`Domicile — ${this.escapeHtml(tour.technician_name)}`, {
            className: 'tour-tooltip',
          })
          .addTo(this.toursLayer)
      }

      // Job step markers (numbered)
      jobSteps.forEach((step, stepIdx) => {
        if (!step.location) return
        const coords: PointTuple = [step.location[1], step.location[0]]
        allPoints.push(coords)

        const isTablot = step.job_type === 'tablot_affair'
        // Masquer le point si son groupe est masqué dans la légende.
        if (isTablot && step.group_name && this.hiddenGroups.has(step.group_name))
          return
        const markerSize = isTablot ? 14 : 10
        const labelNum = stepIdx + 1

        const groupColorMap = this.buildGroupColorMap()
        const stepColor =
          isTablot && step.group_name
            ? this.getGroupColor(step.group_name, groupColorMap)
            : isTablot
              ? color
              : 'rgba(148,163,184,0.6)'

        const icon = L.divIcon({
          className: 'tour-step-icon',
          html: `<div style="
            width:${markerSize * 2}px;height:${markerSize * 2}px;
            border-radius:50%;
            background:${stepColor};
            border:2px solid ${isTablot ? '#fff' : color};
            color:${isTablot ? '#fff' : '#0f172a'};
            font-size:${isTablot ? '11' : '9'}px;
            font-weight:700;
            display:flex;align-items:center;justify-content:center;
            box-shadow:0 2px 6px rgba(0,0,0,0.3);
            opacity:${this.selectedTour ? (isSelected ? 1 : 0.3) : 1};
          ">${labelNum}</div>`,
          iconSize: [markerSize * 2, markerSize * 2],
          iconAnchor: [markerSize, markerSize],
        })

        const marker = L.marker(coords, { icon })
        marker.bindPopup(this.buildStepPopup(step, tour, stepIdx))
        if (isTablot) {
          marker.on('click', () => this.tourSelected.emit(tour))
        }
        marker.addTo(this.toursLayer)
      })

      // End marker (home) — only if different from start
      if (endStep?.location && startStep?.location) {
        // Don't draw end marker on top of start (same location)
      }
    })

    // Fit map: if a tour is selected, zoom to its points only;
    // otherwise include pool markers for the full overview.
    if (!this.selectedTour) {
      this.poolMarkers.forEach((pm) => {
        if (pm.latitude && pm.longitude && !pm.isSelected) {
          allPoints.push([pm.latitude, pm.longitude])
        }
      })
    }

    if (shouldFit && allPoints.length > 0 && this.map) {
      this.map.fitBounds(L.latLngBounds(allPoints), {
        padding: [40, 40],
        maxZoom: 14,
      })
    }

    requestAnimationFrame(() => this.map?.invalidateSize())
  }

  private renderTechnicianHomes(): void {
    if (!this.technicianHomes?.length) return

    this.technicianHomes.forEach((th) => {
      if (!th.latitude || !th.longitude) return

      const coords: PointTuple = [th.latitude, th.longitude]

      const icon = L.divIcon({
        className: 'tech-home-icon',
        html: `<div style="
          width:28px;height:28px;
          border-radius:50%;
          background:#1e293b;
          border:2px solid #64748b;
          color:#e2e8f0;
          font-size:11px;
          font-weight:700;
          display:flex;align-items:center;justify-content:center;
          box-shadow:0 2px 6px rgba(0,0,0,0.3);
        ">${this.escapeHtml(th.initials)}</div>`,
        iconSize: [28, 28],
        iconAnchor: [14, 14],
      })

      L.marker(coords, { icon })
        .bindTooltip(`Domicile — ${this.escapeHtml(th.name)}`, {
          className: 'tour-tooltip',
        })
        .addTo(this.homesLayer)
    })
  }

  private renderPoolMarkers(): void {
    if (!this.poolMarkers?.length) return

    const groupColorMap = this.buildGroupColorMap()
    const factor = this.zoomRadiusFactor()

    this.poolMarkers.forEach((pm) => {
      if (!pm.latitude || !pm.longitude) return
      if (pm.groupName && this.hiddenGroups.has(pm.groupName)) return

      const coords: PointTuple = [pm.latitude, pm.longitude]
      const color = pm.groupName
        ? this.getGroupColor(pm.groupName, groupColorMap)
        : pm.hasSlots
          ? '#10b981'
          : '#94a3b8'

      if (pm.isSelected) {
        // Marqueur sélectionné (unique) : anneau pulsant en divIcon.
        const size = 34
        const html = `
          <div style="position:relative;width:${size}px;height:${size}px;">
            <span class="pool-marker-pulse" style="--pulse-color:${color};"></span>
            <div style="
              position:absolute;
              inset:0;
              margin:auto;
              width:22px;height:22px;
              border-radius:50%;
              background:${color};
              border:3px solid #fff;
              box-shadow:0 0 0 3px ${color},0 4px 14px rgba(0,0,0,0.5);
            "></div>
          </div>`

        const icon = L.divIcon({
          className: 'pool-marker-icon',
          html,
          iconSize: [size, size],
          iconAnchor: [size / 2, size / 2],
        })

        const marker = L.marker(coords, { icon })
        marker.bindPopup(this.buildPoolMarkerPopup(pm))
        marker.on('click', () => this.poolMarkerClicked.emit(pm.affairId))
        marker.addTo(this.poolLayer)
        return
      }

      // Marqueur standard : cercle dessiné sur le canvas partagé (fluide à
      // 1000+ points). Les points hors secteur actif restent visibles, atténués.
      const dimmed = pm.inSector === false
      const base = pm.hasSlots ? 7 : 5.5
      const marker = L.circleMarker(coords, {
        renderer: this.canvasRenderer,
        radius: base * factor,
        color: '#ffffff',
        weight: 1.5,
        opacity: dimmed ? 0.35 : 1,
        fillColor: color,
        fillOpacity: dimmed ? 0.3 : 0.92,
      })
      this.zoomScaledMarkers.push({ marker, base })
      marker.bindPopup(this.buildPoolMarkerPopup(pm))
      marker.on('click', () => this.poolMarkerClicked.emit(pm.affairId))
      marker.addTo(this.poolLayer)
    })
  }

  private buildPoolMarkerPopup(pm: PoolMarker): string {
    const duration =
      pm.bestTourDurationMin != null
        ? `${pm.bestTourDurationMin < 60 ? Math.round(pm.bestTourDurationMin) + ' min' : Math.floor(pm.bestTourDurationMin / 60) + 'h' + (Math.round(pm.bestTourDurationMin % 60) > 0 ? String(Math.round(pm.bestTourDurationMin % 60)).padStart(2, '0') : '')}`
        : ''
    const slotsLabel = pm.hasSlots
      ? `${pm.slotsCount} créneau${pm.slotsCount > 1 ? 'x' : ''}${duration ? ' · ' + duration : ''}`
      : 'Aucun créneau proposé'
    return `
      <div class="tablot-popup">
        <strong>${this.escapeHtml(pm.name)}</strong><br />
        <span>${this.escapeHtml((pm.postalCode ?? '') + ' ' + (pm.locality ?? ''))}</span><br />
        <span>${slotsLabel}</span>
      </div>
    `
  }

  private buildStepPopup(
    step: TablotTourStep,
    tour: TablotTour,
    stepIdx: number
  ): string {
    const time = step.arrival
      ? new Date(step.arrival).toLocaleTimeString('fr-FR', {
          hour: '2-digit',
          minute: '2-digit',
        })
      : '--:--'

    if (step.job_type === 'tablot_affair') {
      return `
        <div class="tablot-popup">
          <strong>Étape ${stepIdx + 1} — ${this.escapeHtml(step.names ?? '')}</strong><br />
          <span>${this.escapeHtml(step.address ?? '')} ${this.escapeHtml(step.postal_code ?? '')} ${this.escapeHtml(step.locality ?? '')}</span><br />
          <span>🕐 ${time} · 🚗 ${step.distance_km?.toFixed(1) ?? '?'} km</span><br />
          <span>👷 ${this.escapeHtml(tour.technician_name)}</span>
        </div>
      `
    }

    return `
      <div class="tablot-popup">
        <strong>Étape ${stepIdx + 1} — ${this.escapeHtml(step.name ?? 'Intervention')}</strong><br />
        <span>🕐 ${time} · Programmée</span><br />
        <span>👷 ${this.escapeHtml(tour.technician_name)}</span>
      </div>
    `
  }

  private formatTourDate(dateStr: string): string {
    const date = new Date(dateStr + 'T00:00:00')
    return date.toLocaleDateString('fr-FR', {
      weekday: 'short',
      day: 'numeric',
      month: 'short',
    })
  }

  /**
   * Decode a Google Encoded Polyline string.
   * @param encoded The encoded polyline string
   * @param precision Number of decimal digits (5 for Google/OSRM, 6 for Valhalla)
   */
  private decodePolyline(encoded: string, precision = 6): PointTuple[] {
    const factor = Math.pow(10, precision)
    const points: PointTuple[] = []
    let index = 0
    let lat = 0
    let lng = 0

    while (index < encoded.length) {
      let shift = 0
      let result = 0
      let byte: number

      do {
        byte = encoded.charCodeAt(index++) - 63
        result |= (byte & 0x1f) << shift
        shift += 5
      } while (byte >= 0x20)
      lat += result & 1 ? ~(result >> 1) : result >> 1

      shift = 0
      result = 0
      do {
        byte = encoded.charCodeAt(index++) - 63
        result |= (byte & 0x1f) << shift
        shift += 5
      } while (byte >= 0x20)
      lng += result & 1 ? ~(result >> 1) : result >> 1

      points.push([lat / factor, lng / factor])
    }

    return points
  }
}
